import { emojis } from 'emoji-mart/data/apple.json';
import BlockTypes from '../../../types/BlockTypes';
import EntityTypes from '../../../types/EntityTypes';
import StyleTypes from '../../../types/StyleTypes';
import { BLOCK, INLINE, TEXT, MARK } from '../../../types/SlateTypes';
import { LINE_BREAK_CHARACTER } from '../../constants';
import getDataDepth from './getDataDepth';
import generateBlockId from '../../utils/generateBlockId';
import { getEmojiNativeText } from '../../utils/emojis';

function unstyledBlock(el, next) {
  if (!el.parentElement || el.parentElement.tagName === 'BODY') {
    return {
      object: BLOCK,
      type: BlockTypes.UNSTYLED,
      data: {
        id: generateBlockId()
      },
      nodes: next(el.childNodes)
    };
  }

  return next(el.childNodes);
}

function emojiFromImage(el) {
  if (el.dataset['stringifyType'] !== 'emoji') return;
  var emojiColon = el.dataset['stringifyEmoji'].replace(/(^:|:$)/g, '');
  var emoji = emojis[emojiColon];
  if (!emoji) return;
  var emojiId = getEmojiNativeText(emoji);
  return {
    object: INLINE,
    type: EntityTypes.EMOJI,
    data: {
      entityId: generateBlockId(),
      emojiId: emojiId,
      emojiColon: emojiColon
    },
    nodes: [{
      object: TEXT,
      text: emojiId
    }]
  };
}

function getElementDimension(element, size) {
  function getAttribute(attribute) {
    return parseInt(element.getAttribute(attribute), 10) || element.getAttribute(attribute) || element.style[attribute].length && element.style[attribute] || undefined;
  }

  return getAttribute(size) || getAttribute(size === 'width' ? 'minWidth' : 'minHeight');
}

var TABLE_CELL = function TABLE_CELL(el, next) {
  var table = el.closest('table');
  var tableId = table.dataset.tableId;
  var rowBreak = !el.nextElementSibling;
  return {
    object: BLOCK,
    type: BlockTypes.TABLE_CELL,
    data: {
      id: generateBlockId(),
      tableId: tableId,
      rowBreak: rowBreak
    },
    nodes: next(el.childNodes)
  };
};

export var TO_BLOCK = {
  DIV: unstyledBlock,
  P: unstyledBlock,
  BLOCKQUOTE: BlockTypes.QUOTE,
  IMG: function IMG(el, next) {
    // A few services uses images for emojis, we can try to parse them with the
    // help of a few accessibility attributes
    var maybeEmoji = emojiFromImage(el);
    if (maybeEmoji) return maybeEmoji;
    var width = getElementDimension(el, 'width');
    var height = getElementDimension(el, 'height');
    return {
      object: BLOCK,
      type: BlockTypes.IMAGE,
      data: {
        loading: true,
        id: generateBlockId(),
        url: el.src,
        width: width,
        height: height
      }
    };
  },
  H1: BlockTypes.H1,
  H2: BlockTypes.H2,
  H3: BlockTypes.H3,
  UL: function UL(el, next) {
    var depth = getDataDepth(el);
    return {
      object: BLOCK,
      type: BlockTypes.UL,
      data: {
        id: generateBlockId(),
        depth: depth
      },
      nodes: next(el.childNodes)
    };
  },
  OL: function OL(el, next) {
    var depth = getDataDepth(el);
    return {
      object: BLOCK,
      type: BlockTypes.OL,
      data: {
        id: generateBlockId(),
        depth: depth
      },
      nodes: next(el.childNodes)
    };
  },
  LI: function LI(el, next) {
    var parent = el.closest('ul, ol');
    var depth = parent ? parseInt(parent.dataset.depth, 10) || 0 : 0;
    return {
      object: BLOCK,
      type: el.parentElement.tagName === 'OL' ? BlockTypes.OL_LI : BlockTypes.UL_LI,
      data: {
        id: generateBlockId(),
        depth: depth
      },
      nodes: next(el.childNodes)
    };
  },
  HR: BlockTypes.SEPARATOR,
  PRE: function PRE(el, next) {
    if (el.childNodes.length === 1 && el.childNodes[0].nodeName === '#text') {
      return {
        object: BLOCK,
        type: BlockTypes.CODES,
        data: {
          id: generateBlockId()
        },
        nodes: el.childNodes[0].nodeValue.split('\n').map(function (text) {
          return {
            object: BLOCK,
            type: BlockTypes.CODE,
            nodes: [{
              object: TEXT,
              text: text
            }]
          };
        })
      };
    }

    return {
      object: BLOCK,
      type: BlockTypes.CODES,
      data: {
        id: generateBlockId()
      },
      nodes: next(el.childNodes)
    };
  },
  TABLE: function TABLE(el, next) {
    el.dataset.tableId = generateBlockId();
    return {
      object: BLOCK,
      type: BlockTypes.TABLE,
      data: {
        id: generateBlockId()
      },
      nodes: next(el.childNodes)
    };
  },
  TD: TABLE_CELL,
  TH: TABLE_CELL
};
export var TO_ENTITY = {
  A: function A(el, next) {
    return {
      object: INLINE,
      type: EntityTypes.LINK,
      data: {
        entityId: generateBlockId(),
        url: el.href
      },
      nodes: next(el.childNodes)
    };
  },
  BR: function BR(el, next) {
    // Strip extra div added by Chrome
    if (el.classList.contains('Apple-interchange-newline')) {
      return next(el.childNodes);
    }

    return {
      object: INLINE,
      type: EntityTypes.LINE_BREAK,
      data: {
        entityId: generateBlockId()
      },
      nodes: [{
        object: TEXT,
        text: LINE_BREAK_CHARACTER
      }]
    };
  }
};
export var TO_MARK = {
  I: StyleTypes.ITALIC,
  EM: StyleTypes.ITALIC,
  U: StyleTypes.UNDERLINE,
  B: StyleTypes.BOLD,
  STRONG: StyleTypes.BOLD,
  DEL: StyleTypes.STRIKETHROUGH,
  S: StyleTypes.STRIKETHROUGH,
  MARK: StyleTypes.HIGHLIGHT,
  CODE: function CODE(el, next) {
    if (el.closest('pre')) {
      return {
        object: BLOCK,
        type: BlockTypes.CODE,
        data: {
          id: generateBlockId()
        },
        nodes: next(el.childNodes)
      };
    }

    return {
      object: MARK,
      type: StyleTypes.CODE,
      nodes: next(el.childNodes)
    };
  }
};
export default {
  TO_BLOCK: TO_BLOCK,
  TO_ENTITY: TO_ENTITY,
  TO_MARK: TO_MARK
};